/* Loading and sampling of the dragon model, owned outside React.
 *
 * The 7.5MB `dragon.glb` download and the ~450,000-point surface sampling used
 * to start only once <DragonPoints> mounted, which is after the intro preloader
 * had already finished. The visitor therefore paid for a polished intro and then
 * watched the dragon pop in late. Hoisting the pipeline into a module lets the
 * work begin the moment the bundle executes, while the intro plays.
 *
 * Two properties matter and are the reason this is not simply a `useEffect`:
 *
 * - It is idempotent and cached. Whoever asks first starts the work; everyone
 *   afterwards - including a <DragonPoints> that mounts much later - gets the
 *   same promise and the same buffers.
 * - The sampling loops yield to the browser between slices. Run in one go the
 *   sampling blocks the main thread for long enough to hitch the very animation
 *   it is meant to be hiding behind (measured at ~150ms unthrottled and ~590ms
 *   under 4x CPU throttling), which would only relocate the jank into the intro.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import {
  makePhases,
  makeUniformDrift,
  makeZeroVec3,
} from "./components/particleHooks";
import { readParams } from "./dragonParams";
import { ASSET_DOWNLOADING, ASSET_READY, ASSET_SAMPLING } from "./revealPolicy";

const MODEL_URL = `${process.env.PUBLIC_URL}/dragon.glb`;

/* dragon.glb ships with a Sphere_BKGND_0 backdrop mesh. Sampling is
   area-weighted, so leaving it in spends nearly the whole point budget on the
   backdrop and inflates the bounding box the dragon is scaled against. */
const EXCLUDED_MESH_PATTERN = /bkgnd|background|backdrop|ground|floor|plane/i;

/* Work is handed back to the browser in slices of this length. Short enough
   that a slice cannot overrun a 60fps frame budget even when it lands next to
   framer-motion's own work; long enough that the yield overhead stays small. */
const SLICE_BUDGET_MS = 5;
/* Iterations between clock reads. Reading the clock every iteration would cost
   more than the sampling itself at this point count. */
const SLICE_STEP = 2048;

const now = () =>
  typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();

/* Idle time is the right place for this: it runs only when the browser has
   nothing more urgent, so the intro's frames always win. The timeout keeps a
   busy main thread from starving the work indefinitely. */
const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && window.requestIdleCallback) {
      window.requestIdleCallback(() => resolve(), { timeout: 100 });
    } else {
      setTimeout(resolve, 0);
    }
  });

/** Runs `body(i)` for i in [0, total), yielding whenever a slice runs long. */
async function sliceLoop(total, body) {
  let i = 0;
  let sliceStart = now();
  while (i < total) {
    const end = Math.min(total, i + SLICE_STEP);
    for (; i < end; i++) body(i);
    if (i < total && now() - sliceStart >= SLICE_BUDGET_MS) {
      await yieldToBrowser();
      sliceStart = now();
    }
  }
}

/* Locates the model's most detail-dense region, which on a creature model is
   the head: faces cluster there for horns, teeth and scales, while body and
   tail are comparatively smooth. Picks the densest cell of a coarse voxel grid,
   then mean-shifts to settle on the cluster's center. */
async function findDetailCenter(geometry) {
  const pos = geometry.getAttribute("position");
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  const size = box.getSize(new THREE.Vector3());
  const diagonal = size.length() || 1;

  const N = 12;
  const cells = new Map();
  const v = new THREE.Vector3();
  const axis = (value, minValue, extent) =>
    Math.min(N - 1, Math.floor(((value - minValue) / (extent || 1)) * N));

  await sliceLoop(pos.count, (i) => {
    v.fromBufferAttribute(pos, i);
    const key =
      axis(v.x, box.min.x, size.x) +
      N * (axis(v.y, box.min.y, size.y) + N * axis(v.z, box.min.z, size.z));
    const cell = cells.get(key) || { n: 0, x: 0, y: 0, z: 0 };
    cell.n += 1;
    cell.x += v.x;
    cell.y += v.y;
    cell.z += v.z;
    cells.set(key, cell);
  });

  let densest = null;
  cells.forEach((cell) => {
    if (!densest || cell.n > densest.n) densest = cell;
  });
  if (!densest) return box.getCenter(new THREE.Vector3());

  const center = new THREE.Vector3(
    densest.x / densest.n,
    densest.y / densest.n,
    densest.z / densest.n,
  );

  const radius = diagonal * 0.15;
  for (let iter = 0; iter < 6; iter++) {
    let n = 0;
    const sum = new THREE.Vector3();
    // eslint-disable-next-line no-await-in-loop
    await sliceLoop(pos.count, (i) => {
      v.fromBufferAttribute(pos, i);
      if (v.distanceTo(center) < radius) {
        sum.add(v);
        n += 1;
      }
    });
    if (n === 0) break;
    center.copy(sum.divideScalar(n));
  }

  return center;
}

/* Biases sampling toward the head. Area-weighted sampling alone gives the head
   only its share of surface area, which is far too few points to resolve a face
   at this scale. MeshSurfaceSampler multiplies face area by the summed weight
   of the face's vertices, so a per-vertex falloff around the head buys detail
   there at the expense of the smooth body. */
async function applyHeadWeights(geometry, headCenter, boost, radiusFraction) {
  const pos = geometry.getAttribute("position");
  geometry.computeBoundingBox();
  const diagonal = geometry.boundingBox.getSize(new THREE.Vector3()).length();
  const radius = Math.max(1e-6, diagonal * radiusFraction);

  const weights = new Float32Array(pos.count);
  const v = new THREE.Vector3();
  await sliceLoop(pos.count, (i) => {
    v.fromBufferAttribute(pos, i);
    const d = v.distanceTo(headCenter) / radius;
    weights[i] = 1 + boost * Math.exp(-d * d);
  });

  geometry.setAttribute("headWeight", new THREE.BufferAttribute(weights, 1));
}

/* Flattens the scene's dragon meshes into one world-space geometry that
   MeshSurfaceSampler can walk. Attributes are stripped to position + normal so
   meshes authored with differing attribute sets still merge cleanly. */
function buildMergedGeometry(scene, includeFur) {
  const collected = [];

  scene.updateMatrixWorld(true);
  scene.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    if (EXCLUDED_MESH_PATTERN.test(child.name)) return;
    if (!includeFur && /fur/i.test(child.name)) return;

    let geometry = child.geometry.clone();
    if (geometry.index) geometry = geometry.toNonIndexed();
    if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();

    const position = geometry.getAttribute("position");
    const normal = geometry.getAttribute("normal");
    const clean = new THREE.BufferGeometry();
    clean.setAttribute("position", position.clone());
    clean.setAttribute("normal", normal.clone());
    clean.applyMatrix4(child.matrixWorld);

    clean.computeBoundingSphere();
    collected.push({
      geometry: clean,
      radius: clean.boundingSphere ? clean.boundingSphere.radius : 0,
    });
  });

  if (collected.length === 0) return null;

  /* Safety net for backdrop meshes the name filter misses: drop anything far
     larger than the median, which is the signature of an enclosing dome. */
  const radii = collected.map((c) => c.radius).sort((a, b) => a - b);
  const median = radii[Math.floor(radii.length / 2)] || 0;
  const kept = collected.filter(
    (c) => median === 0 || c.radius <= median * 2.5,
  );
  const surviving = kept.length > 0 ? kept : collected;
  collected.forEach((c) => {
    if (!surviving.includes(c)) c.geometry.dispose();
  });

  const geometries = surviving.map((c) => c.geometry);
  if (geometries.length === 1) return geometries[0];
  return mergeGeometries(geometries, false);
}

/* Scatters points along the model's creases and contours. Uniform surface
   sampling spreads ink evenly and reads as a smudge; the eye needs lines to
   resolve a shape, so the silhouette and hard edges carry the drawing while
   surface points only supply tone. Segments are chosen with probability
   proportional to length, biased toward the head like the surface pass. */
async function sampleEdgePoints(
  geometry,
  count,
  headCenter,
  boost,
  radiusFraction,
  edgeAngle,
) {
  const edges = new THREE.EdgesGeometry(geometry, edgeAngle);
  const pos = edges.getAttribute("position");
  const segmentCount = Math.floor(pos.count / 2);
  if (segmentCount === 0) {
    edges.dispose();
    return null;
  }

  geometry.computeBoundingBox();
  const diagonal = geometry.boundingBox.getSize(new THREE.Vector3()).length();
  const radius = Math.max(1e-6, diagonal * radiusFraction);

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const mid = new THREE.Vector3();

  // cumulative weights so a segment's odds match its length and head proximity
  const cumulative = new Float32Array(segmentCount);
  let total = 0;
  await sliceLoop(segmentCount, (s) => {
    a.fromBufferAttribute(pos, s * 2);
    b.fromBufferAttribute(pos, s * 2 + 1);
    mid.copy(a).add(b).multiplyScalar(0.5);
    const d = mid.distanceTo(headCenter) / radius;
    total += a.distanceTo(b) * (1 + boost * Math.exp(-d * d));
    cumulative[s] = total;
  });
  if (total <= 0) {
    edges.dispose();
    return null;
  }

  const positions = new Float32Array(count * 3);
  await sliceLoop(count, (i) => {
    const target = Math.random() * total;
    let lo = 0;
    let hi = segmentCount - 1;
    while (lo < hi) {
      const midIdx = (lo + hi) >> 1;
      if (cumulative[midIdx] < target) lo = midIdx + 1;
      else hi = midIdx;
    }
    a.fromBufferAttribute(pos, lo * 2);
    b.fromBufferAttribute(pos, lo * 2 + 1);
    a.lerp(b, Math.random());
    positions[i * 3] = a.x;
    positions[i * 3 + 1] = a.y;
    positions[i * 3 + 2] = a.z;
  });

  edges.dispose();
  return positions;
}

/** Downloads and parses dragon.glb. Split out so the parse step can be timed. */
const fetchModel = () =>
  new Promise((resolve, reject) => {
    new GLTFLoader().load(MODEL_URL, resolve, undefined, reject);
  });

/** The whole pipeline: download, merge, weight, sample, scale. */
async function buildGeometryData(params, onDownloaded) {
  const gltf = await fetchModel();
  onDownloaded();

  const merged = buildMergedGeometry(gltf.scene, params.fur);
  if (!merged) throw new Error("no meshes found in dragon.glb");

  const headCenter = await findDetailCenter(merged);
  await applyHeadWeights(
    merged,
    headCenter,
    params.headBoost,
    params.headRadius,
  );

  const sampler = new MeshSurfaceSampler(new THREE.Mesh(merged))
    .setWeightAttribute(params.headBoost > 0 ? "headWeight" : null)
    .build();

  const count = Math.max(1, Math.floor(params.points));
  const edgeCount = Math.min(count, Math.floor(count * params.edgeRatio));
  const surfaceCount = count - edgeCount;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const drifts = new Float32Array(count);
  const emits = new Float32Array(count); // 0 = fixed, >0 = reach
  const normals = new Float32Array(count * 3); // outward travel direction

  const samplePosition = new THREE.Vector3();
  const sampleNormal = new THREE.Vector3();
  /* Shades are authored as sRGB lightness; three stores colour in a linear
     working space, so the space is named explicitly rather than letting the
     value be read as already-linear. */
  const tint = new THREE.Color();
  const writeColor = (target, offset, lightness, emitting = false) => {
    tint.setHSL(
      emitting ? params.emitHue : params.hue,
      emitting ? params.emitSaturation : params.saturation,
      Math.min(1, Math.max(0.02, lightness)),
      THREE.SRGBColorSpace,
    );
    target[offset] = tint.r;
    target[offset + 1] = tint.g;
    target[offset + 2] = tint.b;
  };
  // camera sits on +Z, so a normal turning away from it marks a silhouette
  // edge, which is where a line drawing carries its weight
  const viewDir = new THREE.Vector3(0, 0, 1);

  await sliceLoop(surfaceCount, (i) => {
    /* Rim term: ~1 where the surface turns away from the camera, ~0 where it
       faces us. Rather than shading by it, reject against it, so the same
       number of points lands unevenly -- crowding at the silhouette and scale
       ridges, thinning over flat facing areas. Density, not brightness, is what
       makes the figure readable. */
    let rim = 0;
    for (let attempt = 0; attempt < 8; attempt++) {
      sampler.sample(samplePosition, sampleNormal);
      rim = 1 - Math.abs(sampleNormal.dot(viewDir));
      if (params.rimDensity <= 0) break;
      const keep =
        params.minDensity +
        (1 - params.minDensity) * Math.pow(rim, params.rimDensity);
      if (Math.random() < keep) break;
    }

    /* A minority of points become emitters: they stay on the surface here and
       the shader walks them outward along this normal, so the model sheds
       particles continuously rather than wearing a fixed halo. Each carries a
       per-particle reach, biased toward short trips so the stream is dense near
       the body and sparse far from it. */
    const emitting =
      params.disperseRatio > 0 && Math.random() < params.disperseRatio;

    positions[i * 3] = samplePosition.x;
    positions[i * 3 + 1] = samplePosition.y;
    positions[i * 3 + 2] = samplePosition.z;

    if (emitting) {
      const reach = Math.pow(Math.random(), params.disperseFalloff);
      emits[i] = reach;
      normals[i * 3] = sampleNormal.x;
      normals[i * 3 + 1] = sampleNormal.y;
      normals[i * 3 + 2] = sampleNormal.z;
      drifts[i] = params.coreDrift + (1 - params.coreDrift) * reach;
    } else {
      drifts[i] = params.coreDrift;
    }

    writeColor(
      colors,
      i * 3,
      (emitting ? params.emitShade : params.fillShade) -
        rim * rim * params.rimStrength +
        (Math.random() - 0.5) * params.shadeJitter,
      emitting,
    );
  });

  const edgePositions =
    edgeCount > 0
      ? await sampleEdgePoints(
          merged,
          edgeCount,
          headCenter,
          params.headBoost,
          params.headRadius,
          params.edgeAngle,
        )
      : null;

  if (edgePositions) {
    await sliceLoop(edgeCount, (i) => {
      const o = (surfaceCount + i) * 3;
      positions[o] = edgePositions[i * 3];
      positions[o + 1] = edgePositions[i * 3 + 1];
      positions[o + 2] = edgePositions[i * 3 + 2];
      // contours never disperse: they are what keeps the outline crisp
      drifts[surfaceCount + i] = params.coreDrift;
      writeColor(
        colors,
        o,
        params.edgeShade + (Math.random() - 0.5) * params.shadeJitter,
      );
    });
  } else if (edgeCount > 0) {
    // no creases found: fall back to filling the budget from the surface
    await sliceLoop(count - surfaceCount, (n) => {
      const i = surfaceCount + n;
      sampler.sample(samplePosition, sampleNormal);
      positions[i * 3] = samplePosition.x;
      positions[i * 3 + 1] = samplePosition.y;
      positions[i * 3 + 2] = samplePosition.z;
      drifts[i] = params.coreDrift;
      writeColor(colors, i * 3, params.fillShade);
    });
  }

  // scale to the target height about the focus pivot
  merged.computeBoundingBox();
  const box = merged.boundingBox;
  const size = box.getSize(new THREE.Vector3());
  const anchor = params.focusHead
    ? headCenter
    : box.getCenter(new THREE.Vector3());
  const pivot = new THREE.Vector3(
    anchor.x + params.focusX * size.x * 0.5,
    anchor.y + params.focusY * size.y * 0.5,
    anchor.z + params.focusZ * size.z * 0.5,
  );
  const scale = params.scale / (size.y || 1);

  await sliceLoop(count, (i) => {
    positions[i * 3] = (positions[i * 3] - pivot.x) * scale;
    positions[i * 3 + 1] = (positions[i * 3 + 1] - pivot.y) * scale;
    positions[i * 3 + 2] = (positions[i * 3 + 2] - pivot.z) * scale;
  });

  /* Soft underlay. In the Shopify reference the speckle sits on top of a
     blurred image, and that base is what holds the shape together; particles
     alone have nothing for the eye to resolve against. Large, faint, heavily
     overlapping sprites approximate that blur without a post-processing pass. */
  const baseCount = Math.max(0, Math.floor(params.baseCount));
  const basePositions = new Float32Array(baseCount * 3);
  const baseColors = new Float32Array(baseCount * 3);
  await sliceLoop(baseCount, (i) => {
    sampler.sample(samplePosition, sampleNormal);
    basePositions[i * 3] = (samplePosition.x - pivot.x) * scale;
    basePositions[i * 3 + 1] = (samplePosition.y - pivot.y) * scale;
    basePositions[i * 3 + 2] = (samplePosition.z - pivot.z) * scale;

    const rim = 1 - Math.abs(sampleNormal.dot(viewDir));
    writeColor(
      baseColors,
      i * 3,
      params.baseShade - rim * rim * params.rimStrength,
    );
  });

  merged.dispose();

  return {
    positions,
    colors,
    count,
    drifts,
    emits,
    normals,
    phases: makePhases(count),
    basePositions,
    baseColors,
    baseCount,
    basePhases: makePhases(baseCount),
    baseDrifts: makeUniformDrift(baseCount, params.coreDrift),
    baseEmits: new Float32Array(baseCount),
    baseNormals: makeZeroVec3(baseCount),
  };
}

let pending = null;
let resolved = null;
let phase = ASSET_DOWNLOADING;
const listeners = new Set();

/**
 * Begins the download and sampling if they have not begun already, and returns
 * the promise for the sampled buffers. Safe to call from anywhere, any number
 * of times: the first call does the work and every later one joins it.
 */
export function startDragonLoad() {
  if (pending) return pending;
  pending = buildGeometryData(readParams(), () => {
    phase = ASSET_SAMPLING;
  })
    .then((data) => {
      resolved = data;
      phase = ASSET_READY;
      listeners.forEach((listener) => listener(data));
      return data;
    })
    .catch((err) => {
      console.error("DragonParticles: failed to prepare dragon.glb", err);
      // A model that will never arrive must not hold the intro open.
      phase = ASSET_READY;
      // A transient failure must not be cached: the next mount starts over.
      pending = null;
      listeners.forEach((listener) => listener(null));
      return null;
    });
  return pending;
}

/** The sampled buffers if they are already in hand, otherwise null. */
export function getDragonGeometry() {
  return resolved;
}

/**
 * How far along the model is, as one of the ASSET_* states the reveal policy
 * reads. The distinction that matters is downloading (open-ended, on a slow
 * link possibly a minute) versus sampling (a fraction of a second).
 */
export function getDragonLoadState() {
  return phase;
}

/** Calls back once when the buffers land. Returns an unsubscribe function. */
export function subscribeDragonGeometry(listener) {
  if (resolved) {
    listener(resolved);
    return () => {};
  }
  listeners.add(listener);
  return () => listeners.delete(listener);
}
